/** Máximo de decimales admitidos en cantidades de Supply (alineado con DecimalField del backend). */
export const SUPPLY_QUANTITY_DECIMALS = 2;

/**
 * Parsea texto de cantidad (admite coma decimal). Devuelve null si vacío o inválido.
 */
export function parseSupplyQuantity(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '' || trimmed === '.' || trimmed === '-') {
    return null;
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  const factor = 10 ** SUPPLY_QUANTITY_DECIMALS;
  return Math.round(n * factor) / factor;
}

/**
 * Piso MOQ al confirmar (blur/Enter) si applyMoqMp está activo.
 * No redondea por MP; solo impide quedar por debajo del mínimo del proveedor.
 */
export function applyMoqFloorOnCommit(
  qty: number,
  moq: number | null | undefined,
  applyMoqMp: boolean,
): number {
  if (!applyMoqMp || qty <= 0) {
    return qty;
  }
  const moqVal = Number(moq) || 0;
  if (moqVal <= 0) {
    return qty;
  }
  return qty < moqVal ? moqVal : qty;
}

/**
 * Mínimo en la rejilla MP que cumple MOQ (misma lógica que sugerido con políticas).
 * p. ej. MOQ 7 + MP 6 → 12; MOQ 10 + MP 6 → 12; MOQ 24 + MP 6 → 24.
 */
export function getSupplyMpAlignedMinimum(
  moq: number | null | undefined,
  mp: number | null | undefined,
  applyMoqMp: boolean,
): number {
  if (!applyMoqMp) {
    return 0;
  }
  const moqVal = Number(moq) || 0;
  if (moqVal <= 0) {
    return 0;
  }
  const mpVal = Number(mp);
  if (!Number.isFinite(mpVal) || mpVal <= 1) {
    return moqVal;
  }
  return Math.ceil(moqVal / mpVal) * mpVal;
}

/** min del input: rejilla MP alineada a MOQ (evita min=7 con pasos de 6). */
export function getSupplyQuantityInputMin(
  moq: number | null | undefined,
  applyMoqMp: boolean,
  mp?: number | null,
): number {
  if (!applyMoqMp) {
    return 0;
  }
  return getSupplyMpAlignedMinimum(moq, mp, true);
}

const MP_GRID_EPSILON = 0.001;

function mpQuantityGridBase(
  moq: number | null | undefined,
  mp: number | null | undefined,
  applyMoqMp: boolean,
): number {
  if (!applyMoqMp) {
    return 0;
  }
  const mpVal = Number(mp);
  if (Number.isFinite(mpVal) && mpVal > 1) {
    return getSupplyMpAlignedMinimum(moq, mp, true);
  }
  const moqVal = Number(moq) || 0;
  return moqVal > 0 ? moqVal : 0;
}

/**
 * Siguiente cantidad en la rejilla MOQ + n·MP (flechas ↑/↓ y spinners).
 * Evita el comportamiento nativo de `<input type="number">`, que hace valor±step
 * sin alinear a múltiplos (p. ej. 25 + MP 6 → 31 en lugar de 30).
 */
export function stepSupplyQuantityByMp(
  current: number,
  direction: 1 | -1,
  mp?: number | null,
  moq?: number | null,
  applyMoqMp = true,
): number {
  const mpVal = Number(mp);
  const moqVal = Number(moq) || 0;

  if (!applyMoqMp || !Number.isFinite(mpVal) || mpVal <= 1) {
    const next = current + direction;
    return applyMoqMp && moqVal > 0 ? Math.max(moqVal, next) : Math.max(0, next);
  }

  const base = mpQuantityGridBase(moq, mp, applyMoqMp);
  let qty = Math.max(0, current);
  if (qty < base) {
    return base;
  }

  const offset = qty - base;
  const nFloor = Math.floor(offset / mpVal + MP_GRID_EPSILON);
  const onGrid = base + nFloor * mpVal;

  if (direction > 0) {
    return onGrid + mpVal;
  }

  if (qty <= onGrid + MP_GRID_EPSILON) {
    const prev = onGrid - mpVal;
    return prev >= base ? prev : base;
  }
  return onGrid;
}

/**
 * Corrige un cambio típico de spinner (↑/↓ nativo: ±1 o ±MP sobre valor desalineado).
 * Si no parece un paso, devuelve la cantidad parseada sin modificar.
 */
export function resolveSupplyQuantityAfterStepInteraction(
  previous: number,
  nextRaw: string,
  mp?: number | null,
  moq?: number | null,
  applyMoqMp = true,
): number | null {
  const next = parseSupplyQuantity(nextRaw);
  if (next === null) {
    return null;
  }

  const mpVal = Number(mp);
  if (!applyMoqMp || !Number.isFinite(mpVal) || mpVal <= 1) {
    return next;
  }

  const diff = next - previous;
  const absDiff = Math.abs(diff);
  const looksLikeNativeStep =
    quantitiesEqual(absDiff, mpVal) || quantitiesEqual(absDiff, 1);

  if (!looksLikeNativeStep) {
    return next;
  }

  const dir: 1 | -1 = diff > 0 ? 1 : -1;
  return stepSupplyQuantityByMp(previous, dir, mp, moq, applyMoqMp);
}

/**
 * @deprecated Usar step="any" + stepSupplyQuantityByMp; el step nativo desalinea MOQ/MP.
 */
export function getSupplyQuantityInputStep(mp?: number | null): number {
  const n = Number(mp);
  if (!Number.isFinite(n) || n <= 1) {
    return 1;
  }
  return n;
}

/** Compara cantidades numéricas con tolerancia (evita falsos positivos por float). */
export function quantitiesEqual(
  a: number | null | undefined,
  b: number | null | undefined,
  epsilon = 0.0001,
): boolean {
  if (a === null || a === undefined) {
    return b === null || b === undefined;
  }
  if (b === null || b === undefined) {
    return false;
  }
  return Math.abs(a - b) < epsilon;
}
